import React, { useState, useEffect } from 'react';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [units, setUnits] = useState([]);
  const [applications, setApplications] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      localStorage.setItem('admin_token', data.token);
      setToken(data.token);
      setIsLoggedIn(true);
      toast.success('Logged in successfully');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Login failed');
    }
  };

  const fetchData = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const [unitsRes, appsRes, leadsRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/units`, { headers }),
        fetch(`${API_URL}/api/admin/applications`, { headers }),
        fetch(`${API_URL}/api/admin/leads`, { headers }),
        fetch(`${API_URL}/api/admin/payments`, { headers })
      ]);

      setUnits(await unitsRes.json());
      setApplications(await appsRes.json());
      setLeads(await leadsRes.json());
      setPayments(await paymentsRes.json());
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch data');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="container max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-[var(--font-display)]">Admin Login</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    required
                    value={credentials.username}
                    onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  />
                </div>
                <Button type="submit" className="w-full bg-[#121416] text-[#FBF8F2] hover:bg-[#1B1F24] rounded-[var(--btn-radius)]">
                  Login
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Default: admin / admin123
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin-only Navigation Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-border">
        <div className="container py-4 flex items-center justify-between">
          <h1 className="text-xl font-[var(--font-display)]">Ellis Row Admin</h1>
          <Button variant="outline" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>
      
      <div className="container py-8">
        <h2 className="text-3xl font-[var(--font-display)] tracking-[-0.02em] mb-8">
          Dashboard
        </h2>

        <Tabs defaultValue="units" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="units">Units</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="units" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Units ({units.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table data-testid="admin-units-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Beds/Baths</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {units.map((unit) => (
                      <TableRow key={unit.id}>
                        <TableCell className="font-medium">{unit.unit_number}</TableCell>
                        <TableCell>{unit.type === 'rental' ? 'Rental' : 'For Sale'}</TableCell>
                        <TableCell>${unit.price.toLocaleString()}</TableCell>
                        <TableCell>{unit.bedrooms} bed / {unit.bathrooms} bath</TableCell>
                        <TableCell>
                          <Badge variant={unit.status === 'available' ? 'default' : 'secondary'}>
                            {unit.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Applications ({applications.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table data-testid="admin-applications-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Fee Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell className="font-medium">{app.applicant_name}</TableCell>
                        <TableCell>{app.applicant_email}</TableCell>
                        <TableCell>{app.applicant_phone}</TableCell>
                        <TableCell>{app.unit_id}</TableCell>
                        <TableCell>
                          <Badge>{app.status}</Badge>
                        </TableCell>
                        <TableCell>{app.application_fee_paid ? '✓' : '✗'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Leads ({leads.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Interest</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {lead.interest_type === 'rental' ? 'Rental' : 'For Sale'}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Payments ({payments.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.session_id}>
                        <TableCell className="font-medium">
                          {payment.payment_type === 'deposit' ? 'Deposit' : 'Application Fee'}
                        </TableCell>
                        <TableCell>${payment.amount}</TableCell>
                        <TableCell>{payment.unit_id}</TableCell>
                        <TableCell>
                          <Badge variant={payment.payment_status === 'paid' ? 'default' : 'secondary'}>
                            {payment.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
